import { CheckIcon } from "@storytell/bsky-icons";
import { type Component, For, Show, createSignal, onMount } from "solid-js";
import { useAppUI } from "~/components/layouts/minimal/AppUIContext";
import { useI18n } from "~/i18n/context";
import { useWire } from "~/wire";

// Define breakpoint widths in pixels
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1920,
  "4xl": 2560,
  "5xl": 3200,
  "6xl": 3840,
  "7xl": 4480,
  "8xl": 5120,
} as const;

// Helper function to safely serialize objects with BigInt values
const safeStringify = (obj: unknown): string => {
  try {
    return JSON.stringify(
      obj,
      (key, value) => {
        // Convert BigInt to string for display
        if (typeof value === "bigint") {
          return `${value.toString()}n`;
        }
        return value;
      },
      2,
    );
  } catch (error) {
    return `[Serialization Error: ${error instanceof Error ? error.message : "Unknown error"}]`;
  }
};

export const BreakpointBug: Component = () => {
  const [dimensions, setDimensions] = createSignal({ width: 0, height: 0 });
  const [showAuthDebug, setShowAuthDebug] = createSignal(false);
  const { currentLanguage } = useI18n();
  const { state, actions } = useAppUI();
  const wire = useWire();

  onMount(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  });

  return (
    <div class="fixed inset-0 pointer-events-none z-[999999999]">
      {/* Gridlines and Labels */}
      <div class="absolute inset-0">
        <For each={Object.entries(BREAKPOINTS)}>
          {([name, width]) => (
            <div class="absolute inset-0">
              {/* Grid line */}
              <div class="absolute top-0 bottom-0 w-px bg-sky-300/20" style={{ left: `${width}px` }} />
              {/* Label */}
              <div
                class="absolute bottom-4 flex items-center gap-1 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm"
                style={{ left: `${width + 4}px` }}
              >
                <span class="text-slate-500">{name}</span>
                <div class="w-4 h-4">
                  <CheckIcon class="hidden sm:block text-green-500" />
                </div>
              </div>
            </div>
          )}
        </For>
      </div>

      {/* Resolution, Language and Debug Controls display */}
      <div class="fixed bottom-4 left-4 flex flex-col gap-1 pointer-events-auto">
        <div class="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm">
          <span class="text-slate-500">
            {dimensions().width}×{dimensions().height}
          </span>
        </div>
        <div class="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm">
          <span class="text-slate-500">{currentLanguage().toUpperCase()}</span>
        </div>

        {/* Rich Text Replacement Toggle */}
        <button
          type="button"
          class="flex items-center gap-2 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm hover:bg-slate-200/90 dark:hover:bg-slate-700/90 transition-colors"
          onClick={() => actions.toggleRichTextReplacement()}
        >
          <span class="text-slate-500">Rich Text:</span>
          <span
            class={
              state.useRichTextReplacement ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            }
          >
            {state.useRichTextReplacement ? "ON" : "OFF"}
          </span>
        </button>

        {/* Auto-Collapse Operations Toggle */}
        <button
          type="button"
          class="flex items-center gap-2 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm hover:bg-slate-200/90 dark:hover:bg-slate-700/90 transition-colors"
          onClick={() => actions.toggleAutoCollapseOperations()}
        >
          <span class="text-slate-500">Auto Collapse:</span>
          <span
            class={
              state.enableAutoCollapseOperations
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }
          >
            {state.enableAutoCollapseOperations ? "ON" : "OFF"}
          </span>
        </button>

        {/* Auth Debug Toggle */}
        <button
          type="button"
          class="flex items-center gap-2 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm hover:bg-slate-200/90 dark:hover:bg-slate-700/90 transition-colors"
          onClick={() => setShowAuthDebug(!showAuthDebug())}
        >
          <span class="text-slate-500">Auth:</span>
          <span
            class={
              wire.auth.isError()
                ? "text-red-600 dark:text-red-400"
                : wire.auth.isLoading()
                  ? "text-yellow-600 dark:text-yellow-400"
                  : wire.auth.isReady()
                    ? "text-green-600 dark:text-green-400"
                    : "text-slate-500"
            }
          >
            {wire.auth.isError()
              ? "ERROR"
              : wire.auth.isLoading()
                ? "LOADING"
                : wire.auth.isReady()
                  ? "READY"
                  : "UNKNOWN"}
          </span>
        </button>

        {/* Debug Mode Indicator */}
        <div class="flex items-center gap-2 px-2 py-1 text-xs font-mono bg-slate-100/90 dark:bg-slate-800/90 rounded backdrop-blur-sm">
          <span class="text-slate-500">Debug:</span>
          <span class={state.isDebugMode ? "text-green-600 dark:text-green-400" : "text-slate-400"}>
            {state.isDebugMode ? "ON" : "OFF"}
          </span>
        </div>
      </div>

      {/* Auth Debug Panel */}
      <Show when={showAuthDebug()}>
        <div class="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-2xl w-full mx-4 pointer-events-auto">
          <div class="bg-slate-100/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg">
            <div class="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
              <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100">Wire Auth Debug</h3>
              <button
                type="button"
                class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                onClick={() => setShowAuthDebug(false)}
              >
                ✕
              </button>
            </div>
            <div class="p-4 space-y-3 max-h-96 overflow-y-auto">
              {/* Auth State Indicators */}
              <div class="grid grid-cols-2 gap-3">
                <div class="space-y-2">
                  <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                    Auth States
                  </h4>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Ready:</span>
                    <span
                      class={
                        wire.auth.isReady() ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }
                    >
                      {wire.auth.isReady() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Loading:</span>
                    <span
                      class={
                        wire.auth.isLoading()
                          ? "text-yellow-600 dark:text-yellow-400"
                          : "text-green-600 dark:text-green-400"
                      }
                    >
                      {wire.auth.isLoading() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Error:</span>
                    <span
                      class={
                        wire.auth.isError() ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
                      }
                    >
                      {wire.auth.isError() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Connecting:</span>
                    <span
                      class={
                        wire.auth.isConnecting()
                          ? "text-yellow-600 dark:text-yellow-400"
                          : "text-green-600 dark:text-green-400"
                      }
                    >
                      {wire.auth.isConnecting() ? "YES" : "NO"}
                    </span>
                  </div>
                </div>

                <div class="space-y-2">
                  <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                    User States
                  </h4>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Guest:</span>
                    <span class={wire.auth.isGuest() ? "text-blue-600 dark:text-blue-400" : "text-slate-500"}>
                      {wire.auth.isGuest() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Registered:</span>
                    <span class={wire.auth.isRegistered() ? "text-green-600 dark:text-green-400" : "text-slate-500"}>
                      {wire.auth.isRegistered() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Pending Verification:</span>
                    <span
                      class={
                        wire.auth.isPendingVerification() ? "text-yellow-600 dark:text-yellow-400" : "text-slate-500"
                      }
                    >
                      {wire.auth.isPendingVerification() ? "YES" : "NO"}
                    </span>
                  </div>

                  <div class="flex items-center justify-between text-xs">
                    <span class="text-slate-600 dark:text-slate-400">Delete Requested:</span>
                    <span class={wire.auth.isDeleteRequested() ? "text-red-600 dark:text-red-400" : "text-slate-500"}>
                      {wire.auth.isDeleteRequested() ? "YES" : "NO"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Token Info */}
              <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
                  Bearer Token
                </h4>
                <div class="text-xs text-slate-600 dark:text-slate-400 font-mono bg-slate-200/50 dark:bg-slate-700/50 p-2 rounded break-all">
                  {wire.auth.token() ? (
                    <span class="text-green-600 dark:text-green-400">
                      {wire.auth.token()?.slice(0, 20)}...{wire.auth.token()?.slice(-8)}
                    </span>
                  ) : (
                    <span class="text-red-600 dark:text-red-400">No token available</span>
                  )}
                </div>
              </div>

              {/* User Info */}
              <Show when={wire.auth.user()}>
                <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
                    User Info
                  </h4>
                  <div class="text-xs text-slate-600 dark:text-slate-400 font-mono bg-slate-200/50 dark:bg-slate-700/50 p-2 rounded">
                    <pre class="whitespace-pre-wrap">{safeStringify(wire.auth.user())}</pre>
                  </div>
                </div>
              </Show>

              {/* Authenticated User Info */}
              <Show when={wire.auth.authenticatedUser()}>
                <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
                    Authenticated User
                  </h4>
                  <div class="text-xs text-slate-600 dark:text-slate-400 font-mono bg-slate-200/50 dark:bg-slate-700/50 p-2 rounded">
                    <pre class="whitespace-pre-wrap">{safeStringify(wire.auth.authenticatedUser())}</pre>
                  </div>
                </div>
              </Show>

              {/* Realm Info */}
              <Show when={wire.auth.realm()}>
                <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <h4 class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
                    Current Realm
                  </h4>
                  <div class="text-xs text-slate-600 dark:text-slate-400 font-mono bg-slate-200/50 dark:bg-slate-700/50 p-2 rounded">
                    <pre class="whitespace-pre-wrap">{safeStringify(wire.auth.realm())}</pre>
                  </div>
                </div>
              </Show>

              {/* Error Info */}
              <Show when={wire.auth.error()}>
                <div class="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <h4 class="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-2">Error</h4>
                  <div class="text-xs text-red-600 dark:text-red-400 font-mono bg-red-100/50 dark:bg-red-900/20 p-2 rounded">
                    {wire.auth.error()}
                  </div>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
